import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, FileText, ReceiptText } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { useTimeEntries } from '../../hooks/queries/useTimeEntries';
import { useGenerateEstimate, useGenerateInvoice } from '../../hooks/queries/useBillingDocuments';
import { formatDuration, computeAmount } from '../../services/timeTracking/timeTrackingService';
import { formatEuros } from '../../services/billing/billingService';
import { cn } from '../../lib/cn';

import { formatAppDate } from '../../lib/dateFormat';
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dossierId: string;
}

type DocMode = 'invoice' | 'estimate';

export function GenerateInvoiceModal({ open, onOpenChange, dossierId }: Props) {
  const navigate = useNavigate();
  const invoiceMut = useGenerateInvoice();
  const estimateMut = useGenerateEstimate();
  const [mode, setMode] = useState<DocMode>('invoice');

  // Toutes les prestations facturables du dossier — pour facture on filtre les non-facturées ;
  // pour devis on montre toutes les billable (un devis ne consomme pas les entries).
  const { data: entries, isLoading } = useTimeEntries({
    dossier_id: dossierId,
    invoiced: mode === 'invoice' ? false : undefined,
  });
  const billable = useMemo(() => (entries ?? []).filter((e) => e.is_billable), [entries]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [vatRate, setVatRate] = useState('20');
  const [dueDateOffset, setDueDateOffset] = useState('30');
  const generateMut = mode === 'invoice' ? invoiceMut : estimateMut;

  // Reset à l'ouverture : pré-sélectionne tout le billable
  useEffect(() => {
    if (open) {
      setSelected(new Set(billable.map((e) => e.id)));
    }
  }, [open, billable]);

  const totals = useMemo(() => {
    let minutes = 0;
    let ht = 0;
    for (const e of billable) {
      if (!selected.has(e.id)) continue;
      minutes += e.duration_minutes;
      const amount = computeAmount(e);
      if (amount !== null) ht += amount;
    }
    const rate = Number(vatRate) || 0;
    const vat = Math.round(ht * (rate / 100) * 100) / 100;
    const ttc = Math.round((ht + vat) * 100) / 100;
    return { minutes, ht, vat, ttc };
  }, [billable, selected, vatRate]);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(billable.map((e) => e.id)));
  }
  function selectNone() {
    setSelected(new Set());
  }

  async function handleGenerate() {
    if (selected.size === 0) {
      toast.error('Sélectionnez au moins une prestation');
      return;
    }
    const rate = Number(vatRate);
    if (Number.isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('Taux TVA invalide');
      return;
    }
    const days = Number(dueDateOffset);
    let dueDate: string | undefined;
    if (!Number.isNaN(days) && days > 0) {
      const d = new Date();
      d.setDate(d.getDate() + days);
      dueDate = d.toISOString().slice(0, 10);
    }

    try {
      const doc = await generateMut.mutateAsync({
        dossier_id: dossierId,
        time_entry_ids: Array.from(selected),
        vat_rate: rate,
        due_date: dueDate ?? null,
      });
      toast.success(mode === 'invoice' ? 'Brouillon de facture créé' : 'Brouillon de devis créé');
      onOpenChange(false);
      navigate(`/facturation/${doc.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur : ${message}`);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
            'w-full max-w-2xl max-h-[90vh] overflow-y-auto',
            'bg-cime-surface rounded-cime border border-neutral-200 shadow-xl',
          )}
        >
          <div className="flex items-center justify-between p-5 border-b border-neutral-200">
            <Dialog.Title className="font-heading text-lg font-semibold text-neutral-900 flex items-center gap-2">
              {mode === 'invoice' ? (
                <FileText className="w-5 h-5 text-cime-primary" />
              ) : (
                <ReceiptText className="w-5 h-5 text-cime-primary" />
              )}
              {mode === 'invoice' ? 'Générer une facture' : 'Générer un devis'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100" aria-label="Fermer">
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* Toggle Facture / Devis */}
          <div className="px-5 pt-4">
            <div className="inline-flex rounded-cime border border-neutral-200 p-0.5 bg-neutral-50">
              <button
                type="button"
                onClick={() => setMode('invoice')}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-[calc(var(--cime-radius)-2px)] transition-colors',
                  mode === 'invoice'
                    ? 'bg-white text-cime-primary shadow-sm font-medium'
                    : 'text-neutral-600 hover:text-neutral-900',
                )}
              >
                Facture
              </button>
              <button
                type="button"
                onClick={() => setMode('estimate')}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-[calc(var(--cime-radius)-2px)] transition-colors',
                  mode === 'estimate'
                    ? 'bg-white text-cime-primary shadow-sm font-medium'
                    : 'text-neutral-600 hover:text-neutral-900',
                )}
              >
                Devis
              </button>
            </div>
            {mode === 'estimate' && (
              <p className="text-xs text-neutral-500 mt-2">
                Un devis n'immobilise pas les prestations : elles restent disponibles pour facturation après acceptation.
              </p>
            )}
          </div>

          <div className="p-5 space-y-4">
            {isLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : billable.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-6">
                Aucune prestation facturable disponible pour ce dossier.
              </p>
            ) : (
              <>
                {/* Toolbar */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-neutral-600">
                    {selected.size} / {billable.length} prestations sélectionnées
                  </p>
                  <div className="flex gap-2">
                    <button onClick={selectAll} className="text-xs text-cime-primary hover:underline">
                      Tout sélectionner
                    </button>
                    <span className="text-xs text-neutral-300">·</span>
                    <button onClick={selectNone} className="text-xs text-neutral-500 hover:underline">
                      Tout désélectionner
                    </button>
                  </div>
                </div>

                {/* Liste */}
                <div className="border border-neutral-200 rounded max-h-[300px] overflow-y-auto">
                  <table className="cime-table text-sm">
                    <thead className="sticky top-0">
                      <tr>
                        <th className="py-2 px-2 w-8"></th>
                        <th className="py-2 px-2">Date</th>
                        <th className="py-2 px-2">Description</th>
                        <th className="py-2 px-2">Durée</th>
                        <th className="py-2 px-2 text-right">HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billable.map((e) => {
                        const amount = computeAmount(e);
                        const isSel = selected.has(e.id);
                        return (
                          <tr
                            key={e.id}
                            onClick={() => toggle(e.id)}
                            className={cn(
                              'cursor-pointer',
                              isSel ? 'bg-cime-gold-bg hover:bg-cime-gold-bg' : 'hover:bg-neutral-50',
                            )}
                          >
                            <td className="py-2 px-2">
                              <input
                                type="checkbox"
                                checked={isSel}
                                onChange={() => toggle(e.id)}
                                onClick={(ev) => ev.stopPropagation()}
                              />
                            </td>
                            <td className="py-2 px-2 text-neutral-700 whitespace-nowrap">
                              {formatAppDate(e.started_at)}
                            </td>
                            <td className="py-2 px-2 text-neutral-700 truncate max-w-[260px]">
                              {e.billing_code && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 mr-1.5">
                                  {e.billing_code}
                                </span>
                              )}
                              {e.description ?? <span className="text-neutral-400 italic">Sans description</span>}
                            </td>
                            <td className="py-2 px-2 tabular-nums text-neutral-700">
                              {formatDuration(e.duration_minutes)}
                            </td>
                            <td className="py-2 px-2 text-right cime-amount">
                              {amount !== null ? formatEuros(amount) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Paramètres */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-neutral-700">Taux TVA (%)</label>
                    <Input type="text" inputMode="decimal" value={vatRate} onChange={(e) => setVatRate(e.target.value)} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-neutral-700">Échéance (jours)</label>
                    <Input type="text" inputMode="numeric" value={dueDateOffset} onChange={(e) => setDueDateOffset(e.target.value)} placeholder="30" />
                  </div>
                </div>

                {/* Totaux */}
                <div className="bg-neutral-50 border border-neutral-200 rounded p-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-neutral-600">Temps total</span><span className="tabular-nums">{formatDuration(totals.minutes)}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-600">Total HT</span><span className="cime-amount">{formatEuros(totals.ht)}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-600">TVA ({vatRate || 0} %)</span><span className="cime-amount">{formatEuros(totals.vat)}</span></div>
                  <div className="flex justify-between border-t border-neutral-300 pt-1 mt-1 font-semibold"><span>Total TTC</span><span className="cime-amount">{formatEuros(totals.ttc)}</span></div>
                </div>
              </>
            )}
          </div>

          <div className="border-t border-neutral-200 p-4 flex gap-3 justify-end">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" disabled={generateMut.isPending}>Annuler</Button>
            </Dialog.Close>
            <Button
              variant="primary"
              isLoading={generateMut.isPending}
              disabled={billable.length === 0 || selected.size === 0}
              onClick={handleGenerate}
            >
              {mode === 'invoice' ? 'Créer le brouillon de facture' : 'Créer le brouillon de devis'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
